import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import ApplyWeddingTheme from "@/components/ApplyWeddingTheme";
import CameraCapture from "@/components/CameraCapture";
import VideoCapture from "@/components/VideoCapture";
import VoiceRecorder from "@/components/VoiceRecorder";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Camera, Check, Heart, Image as ImageIcon, Loader2, Mic, Play, Trash2, Upload, Video } from "lucide-react";

export default function GuestUpload() {
  const { slug } = useParams();
  const [w, setW] = useState(null);
  const [step, setStep] = useState("pick"); // pick | write | uploading | done
  const [items, setItems] = useState([]); // { file, preview, wish, source, voiceFile, voiceDuration, voiceUrl, showVoice }
  const [guestName, setGuestName] = useState("");
  const [applyAll, setApplyAll] = useState("");
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [showCamera, setShowCamera] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const fileRef = useRef(null);
  const videoInputRef = useRef(null);

  useEffect(() => {
    api.get(`/public/weddings/${slug}`).then(r => setW(r.data));
  }, [slug]);

  const addFiles = (files, source = "gallery") => {
    const next = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      wish: "",
      source,
      voiceFile: null,
      voiceDuration: 0,
      voiceUrl: null,
      showVoice: false,
    }));
    setItems((prev) => [...prev, ...next]);
    if (next.length > 0) setStep("write");
  };

  const onSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) addFiles(files, "gallery");
    e.target.value = "";
  };
  const onSelectVideo = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) addFiles(files, "gallery");
    e.target.value = "";
  };

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const setWish = (idx, wish) => setItems((prev) => prev.map((it, i) => i === idx ? { ...it, wish: wish.slice(0, 300) } : it));
  const applyToAll = () => setItems((prev) => prev.map(it => ({ ...it, wish: applyAll.slice(0, 300) })));

  const setVoice = (idx, file, duration) => setItems((prev) => prev.map((it, i) =>
    i === idx ? { ...it, voiceFile: file, voiceDuration: duration, voiceUrl: URL.createObjectURL(file), showVoice: false } : it
  ));
  const clearVoice = (idx) => setItems((prev) => prev.map((it, i) => {
    if (i !== idx) return it;
    if (it.voiceUrl) URL.revokeObjectURL(it.voiceUrl);
    return { ...it, voiceFile: null, voiceDuration: 0, voiceUrl: null, showVoice: false };
  }));
  const toggleVoice = (idx) => setItems((prev) => prev.map((it, i) => i === idx ? { ...it, showVoice: !it.showVoice } : it));

  const startUpload = async () => {
    if (items.length === 0) return;
    setStep("uploading"); setProgress({ done: 0, total: items.length });
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const form = new FormData();
      form.append("file", it.file);
      form.append("guest_name", guestName || "");
      form.append("wish_text", it.wish || "");
      form.append("capture_source", it.source || "gallery");
      if (it.voiceFile) {
        form.append("voice_file", it.voiceFile);
        form.append("voice_duration", String(it.voiceDuration || 0));
      }
      try {
        await api.post(`/public/weddings/${slug}/upload`, form, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (e) {
        toast.error(`Failed to upload ${it.file.name}`);
      }
      setProgress({ done: i + 1, total: items.length });
    }
    setStep("done");
  };

  if (!w) return <div className="min-h-screen grid place-items-center text-neutral-500">Loading...</div>;
  const accent = w.theme?.accent_color || "#C7A76C";

  return (
    <div className="min-h-screen relative pb-24 pt-6 px-4" data-testid="guest-upload-page">
      <ApplyWeddingTheme theme={w.theme} />
      <div className="max-w-2xl mx-auto">
        <Link to={`/w/${slug}`} className="text-xs text-neutral-500 hover:underline inline-flex items-center gap-1" data-testid="upload-back"><ArrowLeft className="w-3 h-3" /> Back to wedding</Link>
        <div className="glass p-6 sm:p-8 mt-3 gloss-highlight">
          <div className="text-xs uppercase tracking-widest text-neutral-500">{w.display_name}</div>
          <h1 className="font-serif text-3xl sm:text-4xl mt-1">{w.upload_button_text}</h1>

          {step === "pick" && (
            <div className="mt-8 fade-in">
              <div className="glass-subtle p-8 sm:p-10 border border-dashed border-white/80 text-center bg-white/70">
                <Camera className="w-8 h-8 mx-auto mb-3" style={{ color: accent }} />
                <div className="font-serif text-xl">Add your memory</div>
                <div className="text-neutral-600 text-sm mt-1">Photo, video, or a heartfelt voice message.</div>
                <div className="grid sm:grid-cols-2 gap-2 mt-6">
                  <button onClick={() => fileRef.current?.click()} className="rounded-full px-5 py-3 text-sm inline-flex items-center justify-center gap-2 bg-white border border-white/95 shadow-sm hover:bg-neutral-50 transition-colors" data-testid="pick-gallery-btn">
                    <ImageIcon className="w-4 h-4" style={{ color: accent }} /> Choose from Gallery
                  </button>
                  <button onClick={() => setShowCamera(true)} className="rounded-full px-5 py-3 text-sm inline-flex items-center justify-center gap-2 bg-white border border-white/95 shadow-sm hover:bg-neutral-50 transition-colors" data-testid="pick-camera-btn">
                    <Camera className="w-4 h-4" style={{ color: accent }} /> Take Photo
                  </button>
                  <button onClick={() => setShowVideo(true)} className="rounded-full px-5 py-3 text-sm inline-flex items-center justify-center gap-2 bg-white border border-white/95 shadow-sm hover:bg-neutral-50 transition-colors" data-testid="pick-video-btn">
                    <Video className="w-4 h-4" style={{ color: accent }} /> Record Video
                  </button>
                  <button onClick={() => videoInputRef.current?.click()} className="rounded-full px-5 py-3 text-sm inline-flex items-center justify-center gap-2 bg-white border border-white/95 shadow-sm hover:bg-neutral-50 transition-colors" data-testid="pick-video-gallery-btn">
                    <Video className="w-4 h-4" style={{ color: accent }} /> Video from Gallery
                  </button>
                </div>
                <div className="text-xs text-neutral-500 mt-4">JPG, PNG, WEBP, HEIC, MP4, MOV, WEBM · up to 60MB each</div>
                <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={onSelect} data-testid="file-input" />
                <input ref={videoInputRef} type="file" multiple accept="video/*" className="hidden" onChange={onSelectVideo} data-testid="video-file-input" />
              </div>
            </div>
          )}

          {step === "write" && (
            <div className="mt-6 fade-in">
              <input className="glass-input rounded-full px-5 py-3 text-sm w-full" placeholder="Your name (optional)" value={guestName} onChange={e=>setGuestName(e.target.value)} data-testid="guest-name-input" />
              <div className="mt-4 flex flex-wrap gap-2 items-end">
                <input className="glass-input rounded-full px-5 py-3 text-sm flex-1 min-w-[220px]" placeholder="Apply one wish to all photos"
                       value={applyAll} onChange={e=>setApplyAll(e.target.value.slice(0,300))} data-testid="apply-all-wish" />
                <button onClick={applyToAll} className="btn-ghost-glass rounded-full px-4 py-2 text-sm" data-testid="apply-all-btn">Apply to all</button>
              </div>
              <div className="mt-5 space-y-4">
                {items.map((it, i) => (
                  <div key={i} className="glass p-4" data-testid={`preview-${i}`}>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="w-full sm:w-40 h-40 rounded-2xl overflow-hidden bg-neutral-200 flex-shrink-0 relative">
                        {it.file.type.startsWith("video") ? (
                          <video src={it.preview} className="w-full h-full object-cover" muted />
                        ) : (
                          <img src={it.preview} alt="" className="w-full h-full object-cover" />
                        )}
                        <div className="absolute top-2 left-2 glass-subtle rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1">
                          {it.file.type.startsWith("video") ? <><Video className="w-3 h-3" /> Video</> : <><ImageIcon className="w-3 h-3" /> Photo</>}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-xs uppercase tracking-widest text-neutral-500">{w.wish_prompt}</div>
                        <textarea rows={3} maxLength={300} value={it.wish} onChange={e=>setWish(i, e.target.value)}
                                  placeholder="Optional. Write something the couple will treasure."
                                  className="glass-input rounded-2xl px-4 py-2 text-sm w-full mt-1" data-testid={`wish-input-${i}`} />
                        <div className="flex items-center justify-between mt-2 text-xs text-neutral-500">
                          <span>{it.wish.length} / 300</span>
                          <div className="flex items-center gap-1">
                            {!it.voiceFile && !it.showVoice && (
                              <button onClick={()=>toggleVoice(i)} className="btn-ghost-glass rounded-full px-3 py-1 inline-flex items-center gap-1" data-testid={`add-voice-${i}`}>
                                <Mic className="w-3 h-3" /> Add voice
                              </button>
                            )}
                            <button onClick={()=>removeItem(i)} className="btn-ghost-glass rounded-full px-3 py-1 inline-flex items-center gap-1" data-testid={`remove-${i}`}>
                              <Trash2 className="w-3 h-3" /> Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {it.showVoice && !it.voiceFile && (
                      <VoiceRecorder
                        onSaved={(file, duration)=>setVoice(i, file, duration)}
                        onCancel={()=>toggleVoice(i)}
                      />
                    )}
                    {it.voiceFile && (
                      <div className="glass-subtle p-3 mt-3 flex items-center gap-3" data-testid={`voice-attached-${i}`}>
                        <span className="w-8 h-8 rounded-full grid place-items-center text-white" style={{ background: accent }}><Mic className="w-4 h-4" /></span>
                        <audio src={it.voiceUrl} controls className="flex-1 h-9" />
                        <button onClick={()=>clearVoice(i)} className="btn-ghost-glass rounded-full p-2" data-testid={`voice-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
                      </div>
                    )}
                  </div>
                ))}

                <div className="flex flex-wrap gap-2">
                  <button onClick={() => fileRef.current?.click()} className="btn-ghost-glass rounded-full px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="add-more-gallery"><ImageIcon className="w-4 h-4" /> Add photo</button>
                  <button onClick={() => setShowCamera(true)} className="btn-ghost-glass rounded-full px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="add-more-camera"><Camera className="w-4 h-4" /> Take photo</button>
                  <button onClick={() => setShowVideo(true)} className="btn-ghost-glass rounded-full px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="add-more-video"><Video className="w-4 h-4" /> Record video</button>
                  <button onClick={() => videoInputRef.current?.click()} className="btn-ghost-glass rounded-full px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="add-more-video-gallery"><Video className="w-4 h-4" /> Video file</button>
                </div>
                <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={onSelect} />
                <input ref={videoInputRef} type="file" multiple accept="video/*" className="hidden" onChange={onSelectVideo} />
              </div>
              <div className="mt-6 flex justify-end">
                <button onClick={startUpload} disabled={items.length===0}
                        className="rounded-full px-6 py-3 text-sm text-white inline-flex items-center gap-2 disabled:opacity-40"
                        style={{ background: accent }} data-testid="upload-submit-btn">
                  <Upload className="w-4 h-4" /> Upload {items.length} memor{items.length===1?"y":"ies"}
                </button>
              </div>
            </div>
          )}

          {step === "uploading" && (
            <div className="mt-8 text-center fade-in">
              <Loader2 className="w-8 h-8 mx-auto animate-spin" style={{ color: accent }} />
              <div className="font-serif text-2xl mt-3">Uploading {progress.done} / {progress.total}</div>
              <div className="text-neutral-600 text-sm">Please keep this page open.</div>
            </div>
          )}

          {step === "done" && (
            <div className="mt-8 text-center fade-in" data-testid="upload-success">
              <div className="w-16 h-16 rounded-full mx-auto grid place-items-center" style={{ background: accent, color: "#fff" }}>
                <Check className="w-8 h-8" />
              </div>
              <div className="font-serif text-3xl mt-4">Memory shared.</div>
              <p className="text-neutral-600 mt-2 max-w-md mx-auto">{w.wish_thank_you}</p>
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <button onClick={()=>{ items.forEach(it => { if (it.voiceUrl) URL.revokeObjectURL(it.voiceUrl); URL.revokeObjectURL(it.preview); }); setItems([]); setStep("pick"); setProgress({done:0,total:0}); }}
                        className="rounded-full px-5 py-2 text-sm text-white" style={{ background: accent }} data-testid="share-another-btn">
                  Share another <ArrowRight className="w-3 h-3 inline ml-1" />
                </button>
                <Link to={`/w/${slug}/memories`} className="btn-ghost-glass rounded-full px-5 py-2 text-sm inline-flex items-center gap-2" data-testid="view-memories-btn">
                  <Heart className="w-3 h-3" /> View memories
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {showCamera && <CameraCapture onClose={()=>setShowCamera(false)} onCapture={(file, source)=>{ addFiles([file], source); setShowCamera(false); }} onFallback={()=>{ setShowCamera(false); fileRef.current?.click(); }} />}
      {showVideo && <VideoCapture onClose={()=>setShowVideo(false)} onCapture={(file, source)=>{ addFiles([file], source); setShowVideo(false); }} onFallback={()=>{ setShowVideo(false); videoInputRef.current?.click(); }} />}
    </div>
  );
}
